import * as React from 'react'
  /* @jsx mdx */
import { mdx } from '@mdx-js/react';
/* @jsxRuntime classic */

/* @jsx mdx */

export const _frontmatter = {};
const layoutProps = {
  _frontmatter
};
const MDXLayout = "wrapper";
export default function MDXContent({
  components,
  ...props
}) {
  return <MDXLayout {...layoutProps} {...props} components={components} mdxType="MDXLayout">
    <h1 {...{
      "id": "gatsby-plugin-theme-ui"
    }}>{`gatsby-plugin-theme-ui`}</h1>
    <p>{`Gatsby plugin for adding Theme UI context`}</p>
    <pre><code parentName="pre" {...{
        "className": "language-sh"
      }}>{`npm i theme-ui gatsby-plugin-theme-ui
`}</code></pre>
    <pre><code parentName="pre" {...{
        "className": "language-js"
      }}>{`// gatsby-config.js
module.exports = {
  plugins: ['gatsby-plugin-theme-ui'],
}
`}</code></pre>
    <p>{`In addition to providing context, this plugin will also
prevent a flash of unstyled colors when using color modes.`}</p>
    <h2 {...{
      "id": "options"
    }}>{`Options`}</h2>
    <table>
      <thead parentName="table">
        <tr parentName="thead">
          <th parentName="tr" {...{
            "align": null
          }}>{`Key`}</th>
          <th parentName="tr" {...{
            "align": null
          }}>{`Default value`}</th>
          <th parentName="tr" {...{
            "align": null
          }}>{`Description`}</th>
        </tr>
      </thead>
      <tbody parentName="table">
        <tr parentName="tbody">
          <td parentName="tr" {...{
            "align": null
          }}><inlineCode parentName="td">{`prismPreset`}</inlineCode></td>
          <td parentName="tr" {...{
            "align": null
          }}><inlineCode parentName="td">{`null`}</inlineCode></td>
          <td parentName="tr" {...{
            "align": null
          }}>{`The name of the preset you'd like to use to style code blocks inside your markdown files. The available presets can be found in the `}<a parentName="td" {...{
              "href": "https://theme-ui.com/packages/prism/"
            }}>{`theme-ui docs`}</a>{`. You can also use a package string of your own choosing.`}</td>
        </tr>
        <tr parentName="tbody">
          <td parentName="tr" {...{
            "align": null
          }}><inlineCode parentName="td">{`preset`}</inlineCode></td>
          <td parentName="tr" {...{
            "align": null
          }}><inlineCode parentName="td">{`null`}</inlineCode></td>
          <td parentName="tr" {...{
            "align": null
          }}>{`This can be a JSON theme object or a string package name. Make sure the package you're requiring is installed in your dependencies.`}</td>
        </tr>
        <tr parentName="tbody">
          <td parentName="tr" {...{
            "align": null
          }}><inlineCode parentName="td">{`injectColorFlashScript`}</inlineCode></td>
          <td parentName="tr" {...{
            "align": null
          }}><inlineCode parentName="td">{`true`}</inlineCode></td>
          <td parentName="tr" {...{
            "align": null
          }}>{`By default, the plugin injects a `}<inlineCode parentName="td">{`script`}</inlineCode>{` tag to prevent color mode flashing. Set this option to `}<inlineCode parentName="td">{`false`}</inlineCode>{` to omit the script. Useful for AMP (Accelerated Mobile Pages) pages.`}</td>
        </tr>
      </tbody>
    </table>
    <blockquote>
      <p parentName="blockquote">{`Note that this plugin assumes the theme object is exported as `}<inlineCode parentName="p">{`default`}</inlineCode>{`.`}</p>
    </blockquote>
    <p>{`The theme module you include in options is considered your base theme. Any further customization and shadowing will be merged with it.`}</p>
    <h3 {...{
      "id": "using-options"
    }}>{`Using options`}</h3>
    <pre><code parentName="pre" {...{
        "className": "language-js"
      }}>{`// gatsby-config.js
module.exports = {
  plugins: [
    {
      resolve: 'gatsby-plugin-theme-ui',
      options: {
        prismPreset: 'night-owl',
        preset: '@theme-ui/preset-funk',
      },
    },
  ],
}
`}</code></pre>
    <h2 {...{
      "id": "customizing-the-theme"
    }}>{`Customizing the theme`}</h2>
    <p>{`To customize the theme used in your Gatsby site,
shadow the `}<inlineCode parentName="p">{`src/gatsby-plugin-theme-ui/index.js`}</inlineCode>{` module.`}</p>
    <pre><code parentName="pre" {...{
        "className": "language-js",
        "metastring": "filename=src/gatsby-plugin-theme-ui/index.js",
        "filename": "src/gatsby-plugin-theme-ui/index.js"
      }}>{`const theme = {
  colors: {
    text: '#111',
    background: '#fff',
  },
}

export default theme
`}</code></pre>
    <h3 {...{
      "id": "load-theme-from-custom-path"
    }}>{`Load theme from custom path`}</h3>
    <p>{`If you prefer to load your theme from a custom path (instead of the standard `}<inlineCode parentName="p">{`src/gatsby-plugin-theme-ui/index.js`}</inlineCode>{`),
you can require it in your `}<inlineCode parentName="p">{`gatsby-config.js`}</inlineCode>{` file:`}</p>
    <pre><code parentName="pre" {...{
        "className": "language-js",
        "metastring": "filename=gatsby-config.js",
        "filename": "gatsby-config.js"
      }}>{`module.exports = {
  plugins: [
    {
      resolve: 'gatsby-plugin-theme-ui',
      options: {
        preset: require('./src/theme'),
      },
    },
  ],
}
`}</code></pre>
    <p>{`Note that `}<inlineCode parentName="p">{`gatsby-config.js`}</inlineCode>{` does not support ES6 modules, so you should use `}<inlineCode parentName="p">{`module.exports`}</inlineCode>{` in your theme file:`}</p>
    <pre><code parentName="pre" {...{
        "className": "language-js",
        "metastring": "filename=src/theme.js",
        "filename": "src/theme.js"
      }}>{`module.exports = {
  colors: {
    text: '#111',
    background: '#fff',
  },
}
`}</code></pre>
    <h2 {...{
      "id": "extending-a-theme"
    }}>{`Extending a theme`}</h2>
    <p>{`To extend a Gatsby theme that uses Theme UI, import the base theme and export a new theme object.`}</p>
    <pre><code parentName="pre" {...{
        "className": "language-js",
        "metastring": "filename=src/gatsby-plugin-theme-ui/index.js",
        "filename": "src/gatsby-plugin-theme-ui/index.js"
      }}>{`import baseTheme from 'gatsby-theme-blog/src/gatsby-plugin-theme-ui'

const theme = {
  ...baseTheme,
  colors: {
    ...baseTheme.colors,
    text: '#111',
    background: '#fff',
  },
}

export default theme
`}</code></pre>
    <p>{`You can also import and use presets from `}<a parentName="p" {...{
        "href": "https://theme-ui.com/packages/presets"
      }}>{`@theme-ui/presets`}</a>{` to use as a starting point.`}</p>
    <h2 {...{
      "id": "color-modes"
    }}>{`Color Modes`}</h2>
    <p>{`To enable support for multiple color modes, add a nested `}<inlineCode parentName="p">{`modes`}</inlineCode>{` object to `}<inlineCode parentName="p">{`theme.colors`}</inlineCode>{`.`}</p>
    <pre><code parentName="pre" {...{
        "className": "language-js",
        "metastring": "filename=src/gatsby-plugin-theme-ui/index.js",
        "filename": "src/gatsby-plugin-theme-ui/index.js"
      }}>{`const theme = {
  colors: {
    text: '#000',
    background: '#fff',
    modes: {
      dark: {
        text: '#fff',
        background: '#000',
      },
    },
  },
}

export default theme
`}</code></pre>
    <h2 {...{
      "id": "components"
    }}>{`Components`}</h2>
    <p>{`Custom MDX components that will receive styles from the theme can be included by adding a `}<inlineCode parentName="p">{`src/gatsby-plugin-theme-ui/components.js`}</inlineCode>{` module.`}</p>
    <pre><code parentName="pre" {...{
        "className": "language-js",
        "metastring": "filename=src/gatsby-plugin-theme-ui/components.js",
        "filename": "src/gatsby-plugin-theme-ui/components.js"
      }}>{`const components = {
  h1: (props) => (
    <h1 {...props}>
      <a href={\`#\${props.id}\`}>{props.children}</a>
    </h1>
  ),
}

export default components
`}</code></pre>
    <p>{`MIT License`}</p>

    </MDXLayout>;
}
;
MDXContent.isMDXComponent = true;
      